import {
  VegaBox,
  VegaButton,
  VegaCard,
  VegaDatePicker,
  VegaFlex,
  VegaGrid,
  VegaInput,
  VegaInputSelect,
} from '@globalpayments/vega-react';

import { ALL_HORIZON_REGIONS } from '../../../types/horizonOptOutTracker';
import type { HorizonOptOutDateRange, HorizonRegionFilter } from '../../../types/horizonOptOutTracker';
import { horizonRegionOptions } from '../constants';

interface HorizonOptOutFiltersProps {
  applicationSearch: string;
  dateRange: HorizonOptOutDateRange;
  isInvalid: boolean;
  onApply: () => void;
  onApplicationSearchChange: (applicationSearch: string) => void;
  onDateRangeChange: (dateRange: HorizonOptOutDateRange) => void;
  onOptOutReasonChange: (optOutReason: string) => void;
  onRegionChange: (region: HorizonRegionFilter) => void;
  optOutReason: string;
  region: HorizonRegionFilter;
}

/** Displays draft date, region, application, and opt-out-reason controls for the tracker. */
export const HorizonOptOutFilters = ({
  applicationSearch,
  dateRange,
  isInvalid,
  onApply,
  onApplicationSearchChange,
  onDateRangeChange,
  onOptOutReasonChange,
  onRegionChange,
  optOutReason,
  region,
}: HorizonOptOutFiltersProps) => (
  <VegaCard backgroundColor="bg-primary" padding="size-16" variant="border">
    <VegaFlex data-testid="horizon-filter-layout" direction="col" gap="size-16">
      <VegaGrid
        column={{ default: 1, M: 2 }}
        data-testid="horizon-primary-filter-grid"
        gap="size-12"
        justifyItems="stretch">
        <VegaDatePicker
          allowRepick
          data-testid="horizon-date-range"
          format="YYYY-MM-DD"
          isValid={isInvalid ? false : undefined}
          label="Date range"
          mode="range"
          onVegaChange={(event) => {
            if (
              Array.isArray(event.detail) &&
              event.detail.length === 2 &&
              event.detail.every((date) => typeof date === 'string')
            ) {
              onDateRangeChange({ endDate: event.detail[1], startDate: event.detail[0] });
            }
          }}
          required
          value={[dateRange.startDate, dateRange.endDate]}
        />
        <VegaInputSelect
          data-testid="horizon-region"
          label="Region"
          onVegaChange={(event) => {
            if (event.detail === ALL_HORIZON_REGIONS || event.detail === 'US' || event.detail === 'UK') {
              onRegionChange(event.detail);
            }
          }}
          required
          selectType="single"
          source={[...horizonRegionOptions]}
          value={region}
        />
      </VegaGrid>
      <VegaGrid
        column={{ default: 1, M: 2, L: 3 }}
        data-testid="horizon-search-filter-grid"
        gap="size-12"
        justifyItems="stretch">
        <VegaInput
          data-testid="horizon-application-search"
          label="Application search"
          onVegaChange={(event) => {
            if (typeof event.detail === 'string') onApplicationSearchChange(event.detail);
          }}
          placeholder="App UID, email, or company name"
          showClearIcon
          value={applicationSearch}
        />
        <VegaInput
          data-testid="horizon-opt-out-reason"
          label="Opt out reason"
          onVegaChange={(event) => {
            if (typeof event.detail === 'string') onOptOutReasonChange(event.detail);
          }}
          placeholder="Search opt out reasons"
          showClearIcon
          value={optOutReason}
        />
        <VegaBox alignSelf="end">
          <VegaButton disabled={isInvalid} label="Apply filters" onVegaClick={onApply} size="small" />
        </VegaBox>
      </VegaGrid>
    </VegaFlex>
  </VegaCard>
);
